import React, { useState, useEffect } from 'react';
import { doc, getDoc, getDocs, collection } from 'firebase/firestore';
import { db, auth } from '../firebaseConfig';
import AddProvider from './AddProvider';
import EditProvider from './EditProvider';
import AddClass from './AddClass';
import HospitalInfo from './HospitalInfo'; // Ensure you import HospitalInfo
import { Box, Typography, Card, CardContent, CardActions, Button, Grid, Dialog } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import EditHospital from './EditHospital'; // Import the new EditHospital component
import ManageUsers from './ManageUsers';
import ManageAddresses from './ManageAddresses'
import TileManager from './TileManager'; // Import the TileManager component
import EditDoctor from './EditDoctor'; // Import the EditDoctor component
import AddDoctor from './AddDoctor'; // Import the AddDoctor component
import ManageActivities from './ManageActivities'; // Import the ManageActivities component

const Admin = () => {
  const [message, setMessage] = useState('');
  const [providers, setProviders] = useState({ ALS: [], BLS: [] });
  const [activeSection, setActiveSection] = useState('');
  const [isAdmin, setIsAdmin] = useState(null);
  const navigate = useNavigate();
  const [hospitals, setHospitals] = useState([]); // State to hold the list of hospitals
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [openEditDoctorDialog, setOpenEditDoctorDialog] = useState(false);
  const [openAddDoctorDialog, setOpenAddDoctorDialog] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    // Function to fetch user role with a 5-second delay
    const fetchUserRole = async () => {
      // Introduce a 5-second delay to allow the auth state to be ready
      setTimeout(async () => {
        const user = auth.currentUser;

        if (!user) {
          setMessage('User not authenticated.');
          setIsAdmin(false);
          return; // Exit early if no user is authenticated
        }

        try {
          console.log('User UID:', user.uid); // Debugging: Log user.uid to ensure it's available
          const userDocRef = doc(db, 'users', user.uid);
          const userDocSnap = await getDoc(userDocRef);

          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            console.log('User Data:', userData);

            if (userData && userData.role) {
              setIsAdmin(userData.role === 'Admin' || userData.role === 'Ops');
            } else {
              setMessage('Role not found for user.');
              setIsAdmin(false);
            }
          } else {
            setMessage('User not found.');
            setIsAdmin(false);
          }
        } catch (error) {
          console.error('Error fetching user role:', error);
          setMessage('Error fetching user role. Please try again.');
          setIsAdmin(false);
        }
      }, 1000); // Delay of 2000 milliseconds (2 seconds)
    };

    fetchUserRole();
  }, []);
  
  
  

  const fetchProviders = async () => {
    try {
      const docRef = doc(db, 'hvac', 'GenericData', 'Providers', 'ProvidersList');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setProviders(docSnap.data());
      }
    } catch (error) {
      console.error('Error fetching providers:', error);
      setMessage('Error fetching providers. Please try again.');
    }
  };

  const fetchHospitals = async () => { // Function to fetch hospitals
    try {
      const docRef = doc(db, 'hvac', 'GenericData', 'Hospitals', 'HospitalsList');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setHospitals(docSnap.data());
      }
    } catch (error) {
      console.error('Error fetching hospitals:', error);
      setMessage('Error fetching hospitals. Please try again.');
    }
  };

  const fetchDoctors = async () => {
    try {
      const doctorsSnapshot = await getDocs(collection(db, 'hvac', 'GenericData', 'Doctors'));
      const doctorsList = doctorsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setDoctors(doctorsList);
    } catch (error) {
      console.error('Error fetching doctors:', error);
    }
  };

  useEffect(() => {
    fetchDoctors();
  }, []);

  const handleOpenEditDoctorDialog = (doctor) => {
    setSelectedDoctor(doctor);
    setOpenEditDoctorDialog(true);
  };

  const handleCloseEditDoctorDialog = () => {
    setOpenEditDoctorDialog(false);
    setSelectedDoctor(null);
  };

  const handleDoctorUpdated = (updatedDoctor) => {
    setDoctors((prevDoctors) =>
      prevDoctors.map((doc) => (doc.id === updatedDoctor.id ? updatedDoctor : doc))
    );
  };

  const handleOpenAddDoctorDialog = () => {
    setOpenAddDoctorDialog(true);
  };

  const handleCloseAddDoctorDialog = () => {
    setOpenAddDoctorDialog(false);
  };

  const handleDoctorAdded = (newDoctor) => {
    setDoctors((prevDoctors) => [...prevDoctors, newDoctor]);
  };

  const handleOpenDialog = (section) => {
    setActiveSection(section);
    setOpenDialog(true);
    // Fetch providers when opening the editProvider dialog
    if (section === 'editProvider') {
      fetchProviders();
    }
    // Fetch hospitals when opening the editHospital dialog
    if (section === 'editHospital') {
      fetchHospitals();
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setActiveSection('');
  };

  // Add a loading state while the user role is being fetched
  if (isAdmin === null) {
    return (
      <Box sx={{ padding: 2 }}>
        <Typography variant="h5">Loading...</Typography>
      </Box>
    );
  }

  // Render error message if user is not an admin
  if (isAdmin === false) {
    return (
      <Box sx={{ padding: 2 }}>
        <Typography variant="h5" color="error" sx={{ fontWeight: 'bold' }}>
          You do not have access to this page. Please contact an administrator.
        </Typography>
      </Box>
    );
  }

  const renderDialogContent = () => {
    switch (activeSection) {
      case 'addProvider':
        return <AddProvider setMessage={setMessage} />;
      case 'editProvider':
        return <EditProvider providers={providers} setMessage={setMessage} fetchProviders={fetchProviders} />;
      case 'manageUsers':
        return <ManageUsers setMessage={setMessage} />;
      case 'ManageAddresses':
        return <ManageAddresses setMessage={setMessage} />;
      case 'addClass':
        return <AddClass setMessage={setMessage} />;
      case 'hospitalInfo':
        return <HospitalInfo setMessage={setMessage} />;
      case 'editHospital':
        return <EditHospital hospitals={hospitals} setMessage={setMessage} onClose={handleCloseDialog} />;
      case 'tileManager':
        return <TileManager />;
      case 'editDoctor':
        return (
          <div>
            <Typography variant="h5">Manage Doctors</Typography>
            <Button variant="contained" color="primary" onClick={handleOpenAddDoctorDialog}>
              Add Doctor
            </Button>
            {doctors.map((doctor) => (
              <Card key={doctor.id} sx={{ margin: 1 }}>
                <CardContent>
                  <Typography variant="h6">{doctor.Name}</Typography>
                  <Typography variant="body2">Specialty: {doctor.Specialty}</Typography>
                  <Typography variant="body2">Phone: {doctor.Phone}</Typography>
                </CardContent>
                <CardActions>
                  <Button onClick={() => handleOpenEditDoctorDialog(doctor)}>Edit</Button>
                </CardActions>
              </Card>
            ))}
          </div>
        );
      case 'manageActivities':
        return <ManageActivities setMessage={setMessage} />;
      default:
        return null;
    }
  };

  return (
    <Box className="admin-dashboard" sx={{ padding: 2, backgroundColor: '#f5f5f5' }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#333' }}>Admin Dashboard</Typography>
      <Grid container spacing={2} justifyContent="center">
        {['addProvider', 'editProvider', 'addClass', 'hospitalInfo', 'editHospital', 'manageUsers', 'ManageAddresses', 'tileManager', 'editDoctor', 'manageActivities'].map((section) => (
          <Grid item xs={12} sm={6} md={4} key={section}>
            <Card sx={{ minWidth: 200, margin: 1, cursor: 'pointer', boxShadow: 3, transition: '0.3s', '&:hover': { boxShadow: 6 } }} onClick={() => handleOpenDialog(section)}>
              <CardContent>
                <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
                  {section.charAt(0).toUpperCase() + section.slice(1).replace(/([A-Z])/g, ' $1')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Click to manage {section.replace(/([A-Z])/g, ' $1').toLowerCase()}.
                </Typography>
              </CardContent>
              <CardActions>
                <Button variant="outlined" color="primary" size="small">Click here</Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="md">
        <Box sx={{ padding: 2 }}>
          {renderDialogContent()}
          <Button onClick={handleCloseDialog} color="primary" variant="contained" sx={{ marginTop: 2 }}>
            Close
          </Button>
        </Box>
      </Dialog>

      <AddDoctor
        open={openAddDoctorDialog}
        onClose={handleCloseAddDoctorDialog}
        onDoctorAdded={handleDoctorAdded}
      />

      <EditDoctor
        doctor={selectedDoctor}
        open={openEditDoctorDialog}
        onClose={handleCloseEditDoctorDialog}
        onDoctorUpdated={handleDoctorUpdated}
      />

      {message && <Typography className={`message ${message.includes('Error') ? 'error' : 'success'}`} sx={{ marginTop: 2 }}>{message}</Typography>}
    </Box>
  );
};

export default Admin;
